
const libPictProvider = require('pict-provider');
const libConfigFieldSchema = require('../views/EmailNotifications-ConfigFieldSchema.js');

/**
 * EmailNotificationsProvider — the data layer behind the config-app Email Notifications (Email Rules) editor.
 *
 * Migrated from the Home Vue app (pages/EmailRules.vue + components/EditEmailRule.vue). An "Email Rule" is a
 * Meadow entity (EmailRule table) — NOT a Configuration blob — that ties a set of documents (Projects +
 * DocumentType + Status filters) to a server-defined notification Handler, optionally on a schedule. The
 * per-rule email content (subject/body/recipients) lives in the Configuration service under
 * 'EmailRule-<Handler>' and is edited via a dynamic form built from the handler's defaultConfiguration.
 *
 * Handlers are SERVER-DRIVEN (GET Email/Handlers) — never hardcoded. Save is a multi-call sequence
 * (EmailRule/Upsert + Configuration/Put + EmailRule/SetSchedule); see the editor-phase methods.
 *
 * State lives in pict.AppData.EmailNotifications; the views derive a clean render-state from it.
 */
class EmailNotificationsProvider extends libPictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;
	}

	get restClient()
	{
		return this.pict.fable.HeadlightRestClient || this.pict.HeadlightRestClient;
	}

	// --- Session context + write-permission (mirrors the ApprovalWorkflow provider / Home's gate) ---

	get currentUser()
	{
		return (this.pict && this.pict.AppData && this.pict.AppData.CurrentUser) || {};
	}

	get idCustomer()
	{
		return this.currentUser.CustomerID;
	}

	get isSuperUser()
	{
		const tmpUser = this.currentUser;
		return (Number(tmpUser.UserRoleIndex) === 5) && (Number(tmpUser.CustomerID) === 1);
	}

	get hasCustomerAdmin()
	{
		const tmpRoles = this.currentUser.UserRoles;
		return Array.isArray(tmpRoles) && tmpRoles.some((pRole) => pRole && (pRole.PermissionLevel === 'CustomerAdmin' || pRole.PermissionLevel === 'SuperAdminUser'));
	}

	/** Capability check via the effective-permissions service (super-user bypass, fail-open before load — UI only). */
	_can(pEntity, pAction)
	{
		const tmpService = this.pict.fable.EffectivePermissionsService;
		return !!(tmpService && (typeof tmpService.can === 'function') && tmpService.can(pEntity, pAction));
	}

	/**
	 * Can the current user create/edit/delete rules? Additive gate — the coarse CustomerAdmin tier OR the
	 * fine-grained `EmailRule-Update` capability (so the Entitlements "Manage email notifications" task works).
	 * UI gate only; the server is authoritative (EmailRule is writable at Director/Executive = MyCustomer).
	 */
	get canEdit()
	{
		return this.isSuperUser || this.hasCustomerAdmin || this._can('EmailRule', 'Update');
	}

	/** Only a customer admin may write the per-rule custom Configuration blob (a config blob, so it stays coarse). */
	get canEditConfig()
	{
		return this.isSuperUser || this.hasCustomerAdmin;
	}

	/** The shared state slice (lazily created). */
	get state()
	{
		if (!this.pict.AppData.EmailNotifications)
		{
			this.pict.AppData.EmailNotifications =
			{
				List: [], ListLoading: false, ListError: '',
				Handlers: null,               // { <handlerName>: { description, docs, defaultConfiguration, scheduled, ... } }
				Lookups: { DocTypeMap: {}, Projects: [], Statuses: [] },
				Editor: null,                 // populated in the editor phase
			};
		}
		return this.pict.AppData.EmailNotifications;
	}

	// --- REST helpers ---

	_getJSON(pURL)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.getJSON(pURL, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	_postJSON(pURL, pPayload)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.postJSON(pURL, pPayload, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	/** EmailRule.Projects / .Status are JSON-array columns typed as string; parse defensively to an array. */
	_parseArray(pValue)
	{
		if (Array.isArray(pValue)) { return pValue; }
		if (typeof pValue === 'string' && pValue.trim())
		{
			try { const tmpParsed = JSON.parse(pValue); return Array.isArray(tmpParsed) ? tmpParsed : []; }
			catch (pErr) { return []; }
		}
		return [];
	}

	// --- Handlers (server-driven registry) ---

	/** Load the handler registry once (GET Email/Handlers). Cached on state.Handlers. */
	async loadHandlersAsync()
	{
		if (this.state.Handlers) { return this.state.Handlers; }
		const tmpHandlers = await this._getJSON('Email/Handlers').catch(() => ({}));
		this.state.Handlers = tmpHandlers || {};
		return this.state.Handlers;
	}

	// --- List ---

	/**
	 * Load every EmailRule for the customer (+ the handler registry + a doc-type name map), as a list of
	 * display rows.
	 */
	async loadListAsync()
	{
		const tmpState = this.state;
		tmpState.ListLoading = true;
		tmpState.ListError = '';
		tmpState.List = [];
		this._repaintList();
		try
		{
			await this.loadHandlersAsync();
			// Doc-type hash → Name, so the list can show a readable Document Type.
			const tmpRNIs = await this._getJSON(`ReportNamedInstances/FilteredTo/FBV~IDCustomer~EQ~${this.idCustomer}`).catch(() => []);
			const tmpDocMap = {};
			(Array.isArray(tmpRNIs) ? tmpRNIs : []).forEach((pRNI) => { if (pRNI && pRNI.Hash) { tmpDocMap[pRNI.Hash] = pRNI.Name || pRNI.Hash; } });
			tmpState.Lookups.DocTypeMap = tmpDocMap;

			const tmpRules = await this._getJSON(`EmailRules/FilteredTo/FBV~IDCustomer~EQ~${this.idCustomer}/0/2000`).catch(() => []);
			tmpState.List = (Array.isArray(tmpRules) ? tmpRules : []).map((pRule) => this.describeRule(pRule));
			tmpState.List.sort((pA, pB) => String(pA.HandlerLabel).localeCompare(String(pB.HandlerLabel)) || (pA.IDEmailRule - pB.IDEmailRule));
		}
		catch (pError)
		{
			tmpState.ListError = `Could not load email rules: ${pError && pError.message ? pError.message : pError}`;
			this.pict.log.warn(`EmailNotifications list load failed`, { error: String(pError) });
		}
		tmpState.ListLoading = false;
		this._repaintList();
	}

	/** Build a readable list row from a raw EmailRule record. */
	describeRule(pRule)
	{
		const tmpProjects = this._parseArray(pRule.Projects);
		const tmpStatus = this._parseArray(pRule.Status);
		const tmpHandlers = this.state.Handlers || {};
		const tmpHandlerMeta = tmpHandlers[pRule.Handler] || {};
		return {
			IDEmailRule: pRule.IDEmailRule,
			Enabled: !!pRule.Enabled,
			Handler: pRule.Handler || '',
			HandlerLabel: this.handlerLabel(pRule.Handler),
			Scheduled: !!tmpHandlerMeta.scheduled,
			DocumentType: pRule.DocumentType || '',
			DocumentTypeText: pRule.DocumentType ? (this.state.Lookups.DocTypeMap[pRule.DocumentType] || pRule.DocumentType) : 'Any document type',
			StatusText: tmpStatus.length ? (tmpStatus.length > 2 ? `${tmpStatus.slice(0, 2).join(', ')} +${tmpStatus.length - 2}` : tmpStatus.join(', ')) : 'Any status',
			ProjectsText: tmpProjects.length ? `${tmpProjects.length} project${tmpProjects.length === 1 ? '' : 's'}` : 'All projects',
			Config: pRule.Config || '',
			Raw: pRule,
		};
	}

	/** A human label for a handler name (spaced out CamelCase). */
	handlerLabel(pHandler)
	{
		if (!pHandler) { return '(no handler)'; }
		return String(pHandler).replace(/([a-z0-9])([A-Z])/g, '$1 $2');
	}

	_repaintList()
	{
		const tmpView = this.pict.views.EmailNotificationsList;
		if (tmpView && tmpView.pict)
		{
			tmpView.render();
		}
	}

	// --- Editor (Phase 2): create / load / save / delete / clone -------------------------------------

	_putJSON(pURL, pPayload)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.putJSON(pURL, pPayload, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	/** DELETE an Entity/<id> path through the rest client's deleteEntity (there is no raw deleteJSON). */
	_deleteEntity(pEntity, pID)
	{
		return new Promise((resolve, reject) =>
		{
			this.restClient.deleteEntity(pEntity, pID, (pError, pBody) => pError ? reject(pError) : resolve(pBody));
		});
	}

	/** Configuration/Get returns { Config, … } or the raw config; normalize to the config object. */
	_unwrapConfig(pResult)
	{
		if (pResult && pResult.Config !== undefined) { return pResult.Config || {}; }
		return pResult || {};
	}

	get editorState()
	{
		if (!this.state.Editor)
		{
			// HadConfig: the rule already had a stored custom-content blob when it loaded. Kept so that clearing
			// every override still writes an empty blob back, rather than leaving the old values in place.
			this.state.Editor = { Rule: null, Loading: false, Error: '', Editable: false, IsNew: false, Dirty: false, HadConfig: false };
		}
		return this.state.Editor;
	}

	_markRuleDirty() { this.editorState.Dirty = true; }

	/** A brand-new rule seeded with the same defaults Home uses (client-only Schedule/ConfigValue fields included). */
	_emptyRule()
	{
		const tmpHandlers = this.state.Handlers || {};
		const tmpDefaultHandler = tmpHandlers.StatusChangeNotification ? 'StatusChangeNotification' : (Object.keys(tmpHandlers)[0] || 'StatusChangeNotification');
		return {
			IDEmailRule: 0, Enabled: true, Projects: [], DocumentType: '', Status: [], Handler: tmpDefaultHandler,
			Config: '', ConfigValue: {}, ScheduleType: 'Weekly', ScheduleDays: [], ScheduleHour: 0,
		};
	}

	/** Load the selects' options (projects, document types, statuses) once. */
	async loadLookupsAsync()
	{
		if (this.state.Lookups.Loaded) { return; }
		await this.loadHandlersAsync();
		// Projects are NOT preloaded — a customer can have thousands (3,334 seen on QA), so the old 25-page (up to
		// 25k) sequential load blocked opening every rule. The projects dual-list is server-side search now (the
		// editor's Available pane queries per keystroke). This cache holds only the projects we've resolved (the
		// rule's selected ones + whatever a search surfaced) so their labels render in the Selected pane; it fills
		// lazily via searchProjectsAsync / resolveSelectedProjectLabelsAsync.
		if (!Array.isArray(this.state.Lookups.Projects)) { this.state.Lookups.Projects = []; }
		if (!this.state.Lookups.ProjectMap) { this.state.Lookups.ProjectMap = {}; }
		const tmpRNIs = await this._getJSON(`ReportNamedInstances/FilteredTo/FBV~IDCustomer~EQ~${this.idCustomer}`).catch(() => []);
		const tmpDocMap = {}; const tmpDocList = [];
		(Array.isArray(tmpRNIs) ? tmpRNIs : []).forEach((pRNI) => { if (pRNI && pRNI.Hash) { tmpDocMap[pRNI.Hash] = pRNI.Name || pRNI.Hash; tmpDocList.push({ Hash: pRNI.Hash, Name: pRNI.Name || pRNI.Hash }); } });
		tmpDocList.sort((pA, pB) => String(pA.Name).localeCompare(String(pB.Name)));
		this.state.Lookups.DocTypeMap = tmpDocMap;
		this.state.Lookups.DocTypes = tmpDocList;
		// Statuses = the standard set + every Status found across the customer's approval workflows. The same
		// workflow blobs are the only place actor tags are authored, so harvest those in the same pass.
		const tmpWorkflows = await this._getJSON(`Configuration/For/ApprovalWorkflow/In/Customer/${this.idCustomer}`).catch(() => ({}));
		const tmpStatuses = new Set([ 'Draft', 'Submitted', 'Reviewed', 'Approved', 'Rejected' ]);
		const tmpActorTags = new Set();
		Object.keys(tmpWorkflows || {}).forEach((pKey) =>
		{
			const tmpBlob = tmpWorkflows[pKey];
			if (!tmpBlob || !Array.isArray(tmpBlob.States)) { return; }
			tmpBlob.States.forEach((pState) =>
			{
				if (!pState) { return; }
				if (pState.Status) { tmpStatuses.add(pState.Status); }
				(Array.isArray(pState.Transitions) ? pState.Transitions : []).forEach((pTransition) =>
				{
					// Object actors carry Tags; keyword/level actors are plain strings and have none.
					(Array.isArray(pTransition && pTransition.Actors) ? pTransition.Actors : []).forEach((pActor) =>
					{
						if (pActor && Array.isArray(pActor.Tags)) { pActor.Tags.forEach((pTag) => { if (pTag) { tmpActorTags.add(String(pTag)); } }); }
					});
				});
			});
		});
		this.state.Lookups.Statuses = Array.from(tmpStatuses);
		this.state.Lookups.ActorTags = Array.from(tmpActorTags).sort((pA, pB) => pA.localeCompare(pB));
		// Roles, for the UsersWithRole picker and the Recipients "Project role" rows.
		const tmpRoles = await this._getJSON(`Roles/FilteredTo/FBV~IDCustomer~EQ~${this.idCustomer}`).catch(() => []);
		// A blank PermissionLevel cannot be targeted — `UsersWithRole` and `ProjectRole:` both match on the
		// level string, so an empty one would collapse every such role into a single meaningless choice.
		this.state.Lookups.Roles = (Array.isArray(tmpRoles) ? tmpRoles : [])
			.filter((pRole) => pRole && (pRole.PermissionLevel != null) && String(pRole.PermissionLevel).trim())
			.map((pRole) => ({ IDRole: pRole.IDRole, PermissionLevel: String(pRole.PermissionLevel), Name: pRole.Name || String(pRole.PermissionLevel) }))
			.sort((pA, pB) => String(pA.Name).localeCompare(String(pB.Name)));
		this.state.Lookups.Loaded = true;
	}

	/**
	 * The distinct PermissionLevels across the customer's roles, with a readable label. `UsersWithRole` and the
	 * `ProjectRole:` recipient token both match on PermissionLevel, and several roles can share one level.
	 * @return {Array<{ PermissionLevel: String, Label: String }>}
	 */
	get permissionLevelOptions()
	{
		const tmpRoles = (this.state.Lookups || {}).Roles || [];
		const tmpByLevel = {};
		tmpRoles.forEach((pRole) =>
		{
			if (!tmpByLevel[pRole.PermissionLevel]) { tmpByLevel[pRole.PermissionLevel] = []; }
			tmpByLevel[pRole.PermissionLevel].push(pRole.Name);
		});
		return Object.keys(tmpByLevel).sort((pA, pB) => pA.localeCompare(pB)).map((pLevel) =>
		{
			const tmpNames = tmpByLevel[pLevel];
			// One role on the level reads best as just its name; several become "Level (A, B)".
			const tmpLabel = (tmpNames.length === 1 && tmpNames[0] !== pLevel) ? `${tmpNames[0]} (${pLevel})` : `${pLevel}${tmpNames.length > 1 ? ` — ${tmpNames.length} roles` : ''}`;
			return { PermissionLevel: pLevel, Label: tmpLabel };
		});
	}

	/** A readable label for a stored UsersWithRole entry, which may be a PermissionLevel string or a numeric IDRole. */
	roleEntryLabel(pEntry)
	{
		const tmpRoles = (this.state.Lookups || {}).Roles || [];
		if (typeof pEntry === 'number' || /^\d+$/.test(String(pEntry)))
		{
			const tmpRole = tmpRoles.find((pRole) => Number(pRole.IDRole) === Number(pEntry));
			return tmpRole ? `${tmpRole.Name} (role ${pEntry})` : `Role ${pEntry}`;
		}
		const tmpLevel = this.permissionLevelOptions.find((pOption) => pOption.PermissionLevel === String(pEntry));
		return (tmpLevel && tmpLevel.Label) || String(pEntry);
	}

	/**
	 * Every project for the customer, paged out of Projects/Lite.
	 *
	 * A single 0/1000 call used to be enough; customers now run into the thousands (QA customer 10 has 3334),
	 * and a project missing from the picker was silently dropped from the rule on save — narrowing or, when
	 * the rule's only project was missing, WIDENING the rule to every project. Paging is what makes the
	 * picker's selected list authoritative.
	 */
	/** Merge resolved projects into the label cache — Lookups.Projects (dual-list Options()) + ProjectMap (labels). */
	_cacheProjects(pProjects)
	{
		const tmpLookups = this.state.Lookups;
		if (!Array.isArray(tmpLookups.Projects)) { tmpLookups.Projects = []; }
		if (!tmpLookups.ProjectMap) { tmpLookups.ProjectMap = {}; }
		(pProjects || []).forEach((pProject) =>
		{
			const tmpID = Number(pProject.IDProject);
			if (!(tmpID > 0) || Object.prototype.hasOwnProperty.call(tmpLookups.ProjectMap, tmpID)) { return; }
			const tmpName = pProject.Name || `Project ${tmpID}`;
			tmpLookups.ProjectMap[tmpID] = tmpName;
			tmpLookups.Projects.push({ IDProject: tmpID, Name: tmpName });
		});
	}

	/** Projects/Lite rows → [{IDProject, Name}]; the Lite shape carries the display name on `Value`. */
	_projectRowsToOptions(pRows)
	{
		return (Array.isArray(pRows) ? pRows : []).map((pRow) => ({ IDProject: pRow.IDProject, Name: pRow.Value || pRow.Name || `Project ${pRow.IDProject}` }));
	}

	/** Customer-scoped project filter, with an optional Name LIKE search term. */
	_projectsFilter(pTerm)
	{
		const tmpTerm = String(pTerm == null ? '' : pTerm).trim();
		const tmpScope = `FBV~IDCustomer~EQ~${this.idCustomer}`;
		return tmpTerm ? `FBV~Name~LK~${encodeURIComponent(`%${tmpTerm}%`)}~${tmpScope}` : tmpScope;
	}

	/** Server-side project search for the dual-list Available pane → [{Value:IDProject, Label:Name}] (first page). */
	async searchProjectsAsync(pTerm)
	{
		const tmpRows = await this._getJSON(`Projects/Lite/FilteredTo/${this._projectsFilter(pTerm)}/0/50`).catch(() => []);
		const tmpProjects = this._projectRowsToOptions(tmpRows);
		this._cacheProjects(tmpProjects);
		return tmpProjects.map((pProject) => ({ Value: pProject.IDProject, Label: pProject.Name }));
	}

	/** "Add all matching the filter" — page the filtered set to exhaustion (bounded by the term) → options. */
	async searchAllProjectsAsync(pTerm)
	{
		const tmpFilter = this._projectsFilter(pTerm);
		const tmpPageSize = 1000;
		const tmpAll = [];
		for (let tmpBegin = 0; ; tmpBegin += tmpPageSize)
		{
			const tmpRows = await this._getJSON(`Projects/Lite/FilteredTo/${tmpFilter}/${tmpBegin}/${tmpPageSize}`).catch(() => []);
			const tmpRowsArr = Array.isArray(tmpRows) ? tmpRows : [];
			tmpRowsArr.forEach((pRow) => tmpAll.push(pRow));
			if (tmpRowsArr.length < tmpPageSize) { break; }
		}
		const tmpProjects = this._projectRowsToOptions(tmpAll);
		this._cacheProjects(tmpProjects);
		return tmpProjects.map((pProject) => ({ Value: pProject.IDProject, Label: pProject.Name }));
	}

	/** Resolve the rule's selected project ids → names into the cache (chunked INN), so the Selected pane reads names
	 * without preloading every project. */
	async resolveSelectedProjectLabelsAsync(pIDs)
	{
		const tmpIDs = (Array.isArray(pIDs) ? pIDs : []).map(Number).filter((pID) => pID > 0);
		if (!tmpIDs.length) { return; }
		const tmpChunkSize = 200;
		for (let tmpI = 0; tmpI < tmpIDs.length; tmpI += tmpChunkSize)
		{
			const tmpChunk = tmpIDs.slice(tmpI, tmpI + tmpChunkSize);
			const tmpRows = await this._getJSON(`Projects/Lite/FilteredTo/FBL~IDProject~INN~${tmpChunk.join(',')}~FBV~IDCustomer~EQ~${this.idCustomer}/0/${tmpChunkSize}`).catch(() => []);
			this._cacheProjects(this._projectRowsToOptions(tmpRows));
		}
	}

	/** A display label for a project id, falling back to the raw id when it is not in the resolved cache. */
	projectLabel(pIDProject)
	{
		return ((this.state.Lookups || {}).ProjectMap || {})[pIDProject] || `Project ${pIDProject}`;
	}

	async newRuleAsync()
	{
		const tmpEditor = this.editorState;
		tmpEditor.Loading = true; tmpEditor.Error = ''; tmpEditor.IsNew = true; tmpEditor.Dirty = false;
		tmpEditor.Editable = this.canEdit; tmpEditor.Rule = null; tmpEditor.HadConfig = false;
		this._repaintEditor();
		await this.loadLookupsAsync();
		tmpEditor.Rule = this._emptyRule();
		tmpEditor.Loading = false;
		this._repaintEditor();
	}

	async loadRuleAsync(pID)
	{
		const tmpEditor = this.editorState;
		tmpEditor.Loading = true; tmpEditor.Error = ''; tmpEditor.IsNew = false; tmpEditor.Dirty = false;
		tmpEditor.Editable = this.canEdit; tmpEditor.Rule = null;
		this._repaintEditor();
		try
		{
			await this.loadLookupsAsync();
			const tmpRows = await this._getJSON(`EmailRules/FilteredTo/FBV~IDEmailRule~EQ~${parseInt(pID, 10)}`).catch(() => []);
			const tmpRaw = (Array.isArray(tmpRows) ? tmpRows : [])[0];
			if (!tmpRaw) { tmpEditor.Error = `Email rule ${pID} was not found.`; tmpEditor.Loading = false; this._repaintEditor(); return; }
			const tmpRule = {
				IDEmailRule: tmpRaw.IDEmailRule, Enabled: !!tmpRaw.Enabled,
				Projects: this._parseArray(tmpRaw.Projects).map(Number), DocumentType: tmpRaw.DocumentType || '',
				Status: this._parseArray(tmpRaw.Status), Handler: tmpRaw.Handler || '', Config: tmpRaw.Config || '',
				ConfigValue: {}, ScheduleType: 'Weekly', ScheduleDays: [], ScheduleHour: 0,
			};
			// Load any existing custom email content so the config editor shows it (and a save round-trips it). A failed
			// read must fail the load: the save writes ConfigValue back whole, so starting from {} would drop the rule's
			// custom content.
			if (tmpRule.Config)
			{
				const tmpConfig = await this._postJSON('Configuration/Get',
					{ LevelContext: 'Customer', LevelContextID: this.idCustomer, NamespaceKey: `EmailRule-${tmpRule.Handler}`, NamespaceValue: tmpRule.Config || 'default' });
				tmpRule.ConfigValue = this._unwrapConfig(tmpConfig) || {};
			}
			tmpEditor.HadConfig = !!(tmpRule.Config && tmpRule.ConfigValue && Object.keys(tmpRule.ConfigValue).length);
			// Load the schedule rows (for scheduled handlers) into the client-only Schedule* fields.
			if ((this.state.Handlers[tmpRule.Handler] || {}).scheduled)
			{
				await this._loadScheduleInto(tmpRule);
			}
			tmpEditor.Rule = tmpRule;
			// Resolve the selected projects' names into the cache so the Selected pane reads names (not "Project <id>")
			// even though we no longer preload every project.
			await this.resolveSelectedProjectLabelsAsync(tmpRule.Projects);
		}
		catch (pError)
		{
			tmpEditor.Error = `Could not load email rule: ${pError && pError.message ? pError.message : pError}`;
			this.pict.log.warn(`EmailNotifications rule load failed`, { id: pID, error: String(pError) });
		}
		tmpEditor.Loading = false;
		this._repaintEditor();
	}

	/** Rebuild the client-only Schedule* fields from the rule's EmailRuleSchedule rows. */
	async _loadScheduleInto(pRule)
	{
		const tmpRows = await this._getJSON(`EmailRuleSchedules/FilteredTo/FBV~IDEmailRule~EQ~${pRule.IDEmailRule}`).catch(() => []);
		if (!Array.isArray(tmpRows) || !tmpRows.length) { return; }
		pRule.ScheduleHour = Number(tmpRows[0].HourOfDay) || 0;
		const tmpDaysOfWeek = tmpRows.filter((pRow) => Number(pRow.DayOfWeek) >= 0).map((pRow) => Number(pRow.DayOfWeek));
		const tmpDaysOfMonth = tmpRows.filter((pRow) => Number(pRow.DayOfMonth) >= 0).map((pRow) => Number(pRow.DayOfMonth));
		if (tmpDaysOfMonth.length) { pRule.ScheduleType = 'Monthly'; pRule.ScheduleDays = tmpDaysOfMonth; }
		else if (tmpDaysOfWeek.length >= 7) { pRule.ScheduleType = 'Daily'; pRule.ScheduleDays = []; }
		else { pRule.ScheduleType = 'Weekly'; pRule.ScheduleDays = tmpDaysOfWeek; }
	}

	/**
	 * The editable config fields for a handler, merging the handler's defaultConfiguration (the field set +
	 * defaults + docs) with the rule's stored ConfigValue.
	 *
	 * Overrides are PER KEY: ConfigValue holds only the keys the admin has chosen to customize, and every
	 * other key falls through to the handler's built-in default at send time. `Overridden` says which is
	 * which, `Value` is what the editor shows (the override when there is one, otherwise the default), and
	 * `Default` is kept alongside so the UI can show what "use the default" actually means.
	 *
	 * @return {Array<{ Key, Value, Default, Overridden, Editor, Doc, KnownField }>}
	 */
	configFieldDescriptors(pHandler, pConfigValue)
	{
		const tmpMeta = (this.state.Handlers || {})[pHandler] || {};
		const tmpDefaults = tmpMeta.defaultConfiguration || {};
		const tmpDocs = tmpMeta.configurationDocs || {};
		const tmpValue = pConfigValue || {};
		const tmpKeys = Object.keys(tmpDefaults);
		// Include any stored keys not in the defaults (forward-compat), preserving default order first.
		Object.keys(tmpValue).forEach((pKey) => { if (tmpKeys.indexOf(pKey) < 0) { tmpKeys.push(pKey); } });
		return tmpKeys.map((pKey) =>
		{
			const tmpOverridden = Object.prototype.hasOwnProperty.call(tmpValue, pKey);
			const tmpDefault = tmpDefaults[pKey];
			return {
				Key: pKey,
				Value: tmpOverridden ? tmpValue[pKey] : tmpDefault,
				Default: tmpDefault,
				Overridden: tmpOverridden,
				Editor: libConfigFieldSchema.resolveFieldEditor(pHandler, pKey, tmpDefault, tmpValue[pKey]),
				Doc: tmpDocs[pKey] || '',
				// A key the handler no longer declares — stored but unknown, so it only gets the JSON escape hatch.
				KnownField: Object.prototype.hasOwnProperty.call(tmpDefaults, pKey),
			};
		});
	}

	/**
	 * Turn a per-key override on (seeding an editable copy of the handler default) or off (dropping the key
	 * so the handler default applies again).
	 */
	setFieldOverride(pKey, pOn)
	{
		const tmpRule = this.editorState.Rule;
		if (!tmpRule) { return; }
		if (!tmpRule.ConfigValue) { tmpRule.ConfigValue = {}; }
		if (pOn)
		{
			const tmpMeta = (this.state.Handlers || {})[tmpRule.Handler] || {};
			const tmpDefault = (tmpMeta.defaultConfiguration || {})[pKey];
			tmpRule.ConfigValue[pKey] = (tmpDefault === undefined) ? '' : JSON.parse(JSON.stringify(tmpDefault));
		}
		else
		{
			delete tmpRule.ConfigValue[pKey];
		}
		this._markRuleDirty();
	}

	/**
	 * Persist the rule: EmailRule/Upsert (+ the new-rule-with-config double-save), then the custom-content
	 * Configuration/Put (customer-admin only, when custom config is in use), then EmailRule/SetSchedule (for
	 * scheduled handlers). Returns { IDEmailRule } or { Error }.
	 * NOTE: IDCustomer is intentionally omitted from the Upsert body — the server derives it from the session.
	 */
	async saveRuleAsync()
	{
		if (!this.canEdit) { throw new Error('You do not have permission to edit email rules.'); }
		const tmpEditor = this.editorState;
		const tmpRule = tmpEditor.Rule;
		if (!tmpRule) { return { Error: 'No rule to save.' }; }
		if (!tmpRule.Handler) { return { Error: 'Choose an email type.' }; }
		const tmpHandlerMeta = (this.state.Handlers || {})[tmpRule.Handler] || {};
		// Per-key overrides: ConfigValue holds only the keys the admin customized. Any override at all means the
		// rule needs its own Config pointer + blob; going back to zero overrides still needs a write (an empty
		// blob), otherwise the previously-stored values would keep applying.
		const tmpOverrideCount = (tmpRule.ConfigValue && typeof tmpRule.ConfigValue === 'object') ? Object.keys(tmpRule.ConfigValue).length : 0;
		const tmpUseConfig = tmpOverrideCount > 0;
		const tmpWriteConfig = tmpUseConfig || !!tmpEditor.HadConfig;

		const tmpBaseBody = () => ({
			Enabled: !!tmpRule.Enabled,
			DocumentType: tmpRule.DocumentType || '',
			Projects: (tmpRule.Projects || []).map(Number),
			Status: tmpRule.Status || [],
			Handler: tmpRule.Handler,
			Config: tmpRule.Config || '',
		});

		let tmpSaved = await this._putJSON('EmailRule/Upsert', Object.assign({ IDEmailRule: tmpRule.IDEmailRule || 0 }, tmpBaseBody()));
		let tmpNewID = (tmpSaved && (tmpSaved.IDEmailRule || (tmpSaved.Record && tmpSaved.Record.IDEmailRule))) || tmpRule.IDEmailRule;

		// Double-save: a brand-new rule with custom config needs its Config pointer set to IDEmailRule-<id>,
		// which is only known after the first Upsert.
		if (!tmpRule.Config && tmpUseConfig && tmpNewID)
		{
			tmpRule.Config = `IDEmailRule-${tmpNewID}`;
			tmpSaved = await this._putJSON('EmailRule/Upsert', Object.assign({ IDEmailRule: tmpNewID }, tmpBaseBody()));
		}
		tmpRule.IDEmailRule = tmpNewID;

		// Custom email content (customer-admin only). Skipped entirely for a rule that has never had overrides,
		// so a plain filter edit never creates a blob for a rule running on the handler defaults.
		// The client-side canEditConfig gate is coarser than the server's (QA showed a customer admin who is
		// refused Configuration writes), and by this point the EmailRule row is ALREADY saved. Letting the
		// rejection throw would surface a raw REST error and hide the fact that the rule now exists — so the
		// failure is captured and reported as a partial save instead.
		let tmpConfigWarning = '';
		if (tmpWriteConfig && this.canEditConfig && tmpRule.Config)
		{
			try
			{
				await this._postJSON(`Configuration/Put/EmailRule-${tmpRule.Handler}/${tmpRule.Config}/Customer/${this.idCustomer}`, tmpRule.ConfigValue || {});
				tmpEditor.HadConfig = tmpUseConfig;
			}
			catch (pError)
			{
				const tmpMessage = (pError && pError.message) ? pError.message : String(pError);
				tmpConfigWarning = /forbidden|permission/i.test(tmpMessage)
					? 'The rule was saved, but its customized email content was not — you do not have permission to write configuration for this customer.'
					: `The rule was saved, but its customized email content was not: ${tmpMessage}`;
				this.pict.log.warn(`EmailNotifications custom-content write failed`, { id: tmpNewID, error: tmpMessage });
			}
		}

		// Schedule (scheduled handlers only).
		if (tmpHandlerMeta.scheduled)
		{
			const tmpDays = tmpRule.ScheduleDays || [];
			await this._postJSON('EmailRule/SetSchedule',
				{
					IDEmailRule: tmpNewID,
					HourOfDay: Number(tmpRule.ScheduleHour) || 0,
					DaysOfMonth: (tmpRule.ScheduleType === 'Monthly') ? tmpDays : false,
					DaysOfWeek: (tmpRule.ScheduleType === 'Daily') ? [ 0, 1, 2, 3, 4, 5, 6 ] : ((tmpRule.ScheduleType === 'Weekly') ? tmpDays : false),
				});
		}

		tmpEditor.IsNew = false;
		tmpEditor.Dirty = false;
		this._repaintEditor();
		return { IDEmailRule: tmpNewID, Warning: tmpConfigWarning };
	}

	async deleteRuleAsync(pID)
	{
		if (!this.canEdit) { throw new Error('You do not have permission to delete email rules.'); }
		await this._deleteEntity('EmailRule', parseInt(pID, 10));
	}

	/**
	 * Clone a rule: a fresh EmailRule with the same targeting, IDClonedRule pointing at the source. The custom
	 * email content is NOT copied (a clone starts on the handler's default config, to avoid two rules sharing
	 * one config pointer); Phase 3's config editor can set it. Returns the new IDEmailRule.
	 */
	async cloneRuleAsync(pRaw)
	{
		if (!this.canEdit) { throw new Error('You do not have permission to clone email rules.'); }
		const tmpBody = {
			IDEmailRule: 0,
			IDClonedRule: pRaw.IDEmailRule,
			Enabled: !!pRaw.Enabled,
			DocumentType: pRaw.DocumentType || '',
			Projects: this._parseArray(pRaw.Projects).map(Number),
			Status: this._parseArray(pRaw.Status),
			Handler: pRaw.Handler,
			Config: '',
		};
		const tmpSaved = await this._putJSON('EmailRule/Upsert', tmpBody);
		return (tmpSaved && (tmpSaved.IDEmailRule || (tmpSaved.Record && tmpSaved.Record.IDEmailRule))) || 0;
	}

	_repaintEditor()
	{
		const tmpView = this.pict.views.EmailNotificationsEditor;
		if (tmpView && tmpView.pict)
		{
			tmpView.render();
		}
	}
}

module.exports = EmailNotificationsProvider;

module.exports.default_configuration =
{
	ProviderIdentifier: 'EmailNotifications',
	AutoInitialize: true,
	AutoInitializeOrdinal: 40,
};
